'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { EcommerceReadySource } from '@/lib/configuration/ecommerce';

/**
 * Resolved e-commerce readiness for the active market.
 *
 * Seeded server-side in the root locale layout (above `GoogleAnalyticsProvider`
 * so the GTM `useGoogleAnalytics` hook can read it) and consumed by deeply
 * nested client components — product cards, header, GTM — instead of each one
 * re-deriving catalog mode from the URL locale.
 */
export interface EcommerceReadyValue {
  /** Whether the current market may transact (show cart, add-to-bag, etc.). */
  ready: boolean;
  /** Where the decision came from; `legacy` means the kill-switch is off. */
  source: EcommerceReadySource;
}

/**
 * Default when no provider is mounted (Storybook / isolated tests): ready +
 * legacy. This is byte-identical to today's behavior — commerce enabled and
 * GTM keeps its hardcoded `market_ecom` map — so unwrapped consumers never
 * regress.
 */
const DEFAULT_VALUE: EcommerceReadyValue = { ready: true, source: 'legacy' };

const EcommerceReadyContext = createContext<EcommerceReadyValue>(DEFAULT_VALUE);

export function EcommerceReadyProvider({
  value,
  children,
}: Readonly<{ value: EcommerceReadyValue; children: ReactNode }>) {
  return <EcommerceReadyContext.Provider value={value}>{children}</EcommerceReadyContext.Provider>;
}

/** Read the active market's e-commerce readiness from context. */
export function useEcommerceReady(): EcommerceReadyValue {
  return useContext(EcommerceReadyContext);
}
